/* Opt-in case presentation only. Controllers and answer handoffs stay in this closure.
 * Host ordering and local-only verification limits: docs/case-selector/UI.md. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else if (!root.ScCaseSelector) {
    root.ScCaseSelector = factory();
    if (root.document && typeof root.document.dispatchEvent === 'function' && root.CustomEvent) {
      root.document.dispatchEvent(new root.CustomEvent('sc:case-assets-ready'));
    }
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';
  var instances = new WeakMap();
  var pending = new WeakMap(); // keyed by the EXACT destination mount, never a global section id
  var attachmentWaiters = new WeakMap();
  var formLocks = new WeakMap();
  var failedControlLocks = new WeakMap();
  function formLock(form, on) {
    if (!form) return;
    if (on) {
      var locked = formLocks.get(form) || [];
      var section = form.closest('section[data-section]'), id = form.getAttribute('id');
      var nodes = [form];
      if (section) {
        Array.prototype.forEach.call(section.querySelectorAll('[form]'), function (node) {
          if (node.getAttribute('form') !== id) return;
          nodes.push(node);
          var stepper = node.getAttribute('name') === 'quantity' && node.closest('quantity-input');
          if (stepper && stepper.closest('section[data-section]') === section) nodes.push(stepper);
        });
        Array.prototype.push.apply(nodes, section.querySelectorAll('[data-sc-product-upsells]'));
      }
      nodes.forEach(function (node) {
        if (!locked.some(function (row) { return row.node === node; })) locked.push({ node: node, inert: node.inert === true });
        node.inert = true;
      });
      formLocks.set(form, locked);
    } else if (formLocks.has(form)) {
      formLocks.get(form).forEach(function (row) { row.node.inert = row.inert; });
      formLocks.delete(form);
    }
  }
  var serial = 0;
  // Card names match the cart/checkout/order variant names (owner 29/10). Prices always come from Shopify variants.
  var CARD_TITLES = { snap: 'Slim Case', tough: 'Tough Case', magsafe: 'Tough MagSafe Case' };
  // Theme settings live on each section; copy is never pinned into this JavaScript.
  var TOUGH_FEATURES = [
    'Dual-Layer Protection: Shatterproof plastic exterior and shock-absorbing black silicone inner lining.',
    'Raised Bezel for Screen Protection \u2013 and around the camera to keep it safe from scratches.',
    'Perfect Fit: Specifically made for your phone model, allowing easy access to all buttons and ports.',
    'Exceptional Print Quality: High-quality, fully 3D wrapped design.',
    'Eco-Friendly: Made with REACh, RoHS, and TUVRheinland certified materials.'];
  var CASE_FEATURES = { tough: TOUGH_FEATURES, magsafe: TOUGH_FEATURES.concat('MagSafe Compatible: Works with MagSafe chargers and accessories.') };
  var CARD_LEGENDS = { snap: 'Slim Case', tough: 'Tough Case', magsafe: 'Tough MagSafe Case' };
  var identityKeys = ['designId', 'releaseId', 'formatId', 'formatSchemaVersion'];
  function fail() { throw new Error('Case selection unavailable'); }
  function same(a, b) { return String(a) === String(b); }
  function clone(value) { return JSON.parse(JSON.stringify(value)); }
  function one(node, selector) {
    var found = node.querySelectorAll(selector);
    if (found.length !== 1) fail();
    return found[0];
  }
  function json(node, selector) {
    var el = node.querySelector(selector);
    return el ? JSON.parse(el.textContent) : null;
  }
  function modelMatches(a, b) {
    return a && b && ['optionSetId', 'optionId', 'v'].every(function (key) { return same(a[key], b[key]); });
  }
  function assertSource(source, release, mount) {
    if (!source || !release || !release.product || !source.identity
      || !same(source.productId, release.product.shopifyProductId)
      || !same(source.productId, mount.getAttribute('data-pz-product-id'))
      || source.releaseUrl !== mount.getAttribute('data-pz-release-url')
      || !identityKeys.every(function (key) { return source.identity[key] != null && same(source.identity[key], release.identity[key]); })) fail();
  }
  function prepare(args) {
    var mount = args.mount, release = args.release, cfg = args.cfg || {};
    // Order reopen and Edit are deliberately NOT another case-selector entry point.
    if (!mount || cfg.editMode || cfg.restoreMode || cfg.rawRestoreIntent || cfg.restoreStateId) return null;
    if (instances.has(mount)) return instances.get(mount);
    var section = mount.closest('section[data-section]');
    if (!section) return null;
    var blocks = section.querySelectorAll('sc-case-selector');
    if (!blocks.length) return null;
    var block = blocks[0], doc = mount.ownerDocument, win = doc.defaultView, pageWindow = win;
    var incoming = pending.get(mount) || null;
    var handoffComplete = !incoming;
    var dead = false, attached = false, ready = false, good = false, busy = false;
    var controller, controls, core, catalog, source, selectedModel = '', selectedCase = '', brand = '';
    var preferred = block.getAttribute('data-default-case') || 'tough';
    var sequence = 0, flight = null, transferring = false, restored = false, fatal = false, admitted = false;
    var offs = [], hidden = [], products = Object.create(null), prices = Object.create(null);
    var confirmations = incoming && incoming.confirmations ? clone(incoming.confirmations) : {};
    // Keep case-dependent finish choices privately while visiting a type with no Surface.
    // Main answers remain in the real controller; no shopper text is stored in this memo.
    var surfaceMemory = incoming && incoming.surfaceMemory || Object.create(null);
    var offerChoices = incoming && incoming.offerChoices || [];
    var presentationApi = pageWindow.ScCasePresentation;
    var presentation = presentationApi && typeof presentationApi.prepare === 'function'
      ? presentationApi.prepare({ section: section, block: block }) : null;
    function requirePresentation() {
      if (!presentation && (block.querySelector('script[data-sc-case-media]')
        || section.querySelector('template[data-sc-case-description-for]'))) fail();
    }
    var restorePromise = null;
    var lifetime = new win.AbortController(), observer, ui, brandSelect, modelSelect, modelTrigger, modelValue, sheet, closing = [], cards, status, retry, caseLegend;
    var uid = 'sc-case-' + (++serial), rawSectionId = section.getAttribute('data-section');
    var picker = section.querySelector('variant-selects');
    var originalUpdateURL = picker && picker.getAttribute('data-update-url');
    var ownedForm = doc.getElementById(cfg.formId || mount.getAttribute('data-pz-form-id'));
    if (ownedForm && ownedForm.closest('section[data-section]') !== section) ownedForm = null;
    var originalInert;
    function live() { return !dead && (!doc.contains || doc.contains(mount)); }
    function event(node, type, detail) {
      node.dispatchEvent(new win.CustomEvent(type, { bubbles: true, detail: detail }));
    }
    function listen(node, type, fn, capture) {
      node.addEventListener(type, fn, capture);
      offs.push(function () { node.removeEventListener(type, fn, capture); });
    }
    // Routine progress stays screen-reader only; problems the shopper must act on are shown.
    function message(text, show) { if (status) { status.textContent = text; status.classList.toggle('sc-case-selector__status--shown', !!show); } }
    // Same-release switches (Tough <-> MagSafe) make the runtime re-render once without a model
    // (Surface gone, ~0.5-1s) before the model is re-confirmed. Owner 29/10: no flicker, so a
    // static inert copy holds the old answers on screen until the switch settles.
    var frozen = null, frozenOldDisplay = '', frozenOldPriority = '', frozenTimer = 0;
    function freeze() {
      if (frozen || !controls || !controls.parentNode) return;
      frozen = controls.cloneNode(true);
      frozen.removeAttribute('data-pz-controls');
      frozen.querySelectorAll('[id], [name], [form], [data-pz-control]').forEach(function (n) {
        ['id', 'name', 'form', 'data-pz-control'].forEach(function (a) { n.removeAttribute(a); });
      });
      frozen.setAttribute('aria-hidden', 'true'); frozen.inert = true;
      controls.parentNode.insertBefore(frozen, controls.nextSibling);
      // !important: the theme pins .pz-configurator__body to display:flex !important.
      frozenOldDisplay = controls.style.getPropertyValue('display'); frozenOldPriority = controls.style.getPropertyPriority('display');
      controls.style.setProperty('display', 'none', 'important');
      frozenTimer = win.setTimeout(unfreeze, 8000);
    }
    function unfreeze() {
      if (!frozen) return;
      win.clearTimeout(frozenTimer);
      frozen.remove(); frozen = null;
      if (controls) controls.style.setProperty('display', frozenOldDisplay, frozenOldPriority);
    }
    function lock(on) {
      busy = on;
      if (!on) unfreeze();
      block.setAttribute('aria-busy', on ? 'true' : 'false');
      if (controls) controls.inert = on || originalInert || !handoffComplete;
      formLock(ownedForm, on || !handoffComplete);
    }
    function clearBootRecovery() {
      Array.prototype.forEach.call(block.querySelectorAll('[data-sc-case-boot-recovery]'), function (node) { node.remove(); });
    }
    function publishCase(caseKey) {
      if (caseKey) {
        requirePresentation();
        if (presentation) presentation.apply(caseKey);
        block.setAttribute('data-sc-case-selected', caseKey);
        var upsells = pageWindow.__scUpsellProduct;
        // restoreChoices scans eligibility synchronously from the committed marker. Notify
        // bootstrap only AFTER choices/fields are restored, never midway through consent.
        if (upsells && upsells.restoreChoices) upsells.restoreChoices(section, offerChoices);
        good = true; handoffComplete = true;
        mount.removeAttribute('data-sc-case-failed'); clearBootRecovery();
        if (incoming) { pending.delete(mount); incoming.selection = null; incoming.sourceRelease = null; }
      } else { good = false; block.removeAttribute('data-sc-case-selected'); }
      event(block, 'sc:case-change');
    }
    function rememberOffers() {
      if (!handoffComplete) return; // Failed destination defaults are not new shopper consent.
      var upsells = pageWindow.__scUpsellProduct;
      if (!upsells || !upsells.captureChoices) return;
      var current = upsells.captureChoices(section);
      var keys = new Set(current.map(function (c) { return c.kind + '/' + c.productId; }));
      offerChoices = offerChoices.filter(function (c) { return !keys.has(c.kind + '/' + c.productId); }).concat(current);
    }
    function abort() {
      unfreeze();
      rememberOffers();
      publishCase(null);
      sequence++;
      if (flight) flight.abort();
      flight = null;
      good = false;
      lock(false);
    }
    function error() {
      publishCase(null);
      good = false;
      lock(false);
      message('We could not apply this case. Your selection is not ready to purchase. Please retry.', true);
      if (retry) retry.hidden = false;
    }
    function request(url, signal, kind) {
      return new Promise(function (resolve, reject) {
        var local = new pageWindow.AbortController(), settled = false;
        var timer = pageWindow.setTimeout(cancel, 12000);
        function finish(value, error) {
          if (settled) return;
          settled = true; pageWindow.clearTimeout(timer);
          signal.removeEventListener('abort', cancel);
          if (error) reject(error); else resolve(value);
        }
        function cancel() { local.abort(); finish(null, new Error('Case selection unavailable')); }
        signal.addEventListener('abort', cancel, { once: true });
        if (signal.aborted) { cancel(); return; }
        Promise.resolve().then(function () {
          if (settled) fail();
          return win.fetch(url, { method: 'GET', credentials: 'same-origin', cache: kind === 'catalog' ? 'default' : 'no-store',
            signal: local.signal, headers: { Accept: kind === 'html' ? 'text/html' : 'application/json' } });
        }).then(function (response) { if (!response.ok) fail(); return response.text(); })
          .then(function (text) { finish(kind === 'html' ? text : JSON.parse(text)); })
          .catch(function (error) { finish(null, error); });
      });
    }
    function productURL(handle, suffix) {
      if (!/^[a-z0-9][a-z0-9-]*$/.test(handle || '')) fail();
      var route = win.Shopify && win.Shopify.routes && win.Shopify.routes.root || '/';
      if (!/^\/(?:[a-zA-Z0-9_-]+\/)*$/.test(route)) fail();
      return new URL(route + 'products/' + handle + (suffix || ''), win.location.origin);
    }
    function hide(node) {
      if (!node || hidden.some(function (row) { return row.node === node; })) return;
      hidden.push({ node: node, old: node.getAttribute('data-sc-case-native-hidden') });
      node.setAttribute('data-sc-case-native-hidden', 'true');
    }
    function legacyKeys() {
      var keys = [];
      (release.selection && release.selection.optionSets || []).forEach(function (set) {
        if (set.role !== 'phone-model') return;
        (set.options || []).forEach(function (option) {
          // Match core's exact legacy companion contract; never hide an unrelated main add-on.
          var functions = option.functions || [], fn = functions[0];
          if (option.type === 'Checkbox' && functions.length === 1 && fn.type === 'shProduct'
            && Array.isArray(fn.shProducts) && fn.shProducts.length === 1
            && fn.shProducts[0].handle === 'make-it-magsafe') keys.push(set.id + '/' + option.id);
        });
      });
      return keys;
    }
    var magsafeKeys = legacyKeys();
    function hideNative() {
      if (!ready || dead) return;
      controls.querySelectorAll('[data-pz-field="model"]').forEach(hide);
      var nativeNames = [release.product.variationName];
      (release.product.variantAxes || []).forEach(function (axis) {
        if (axis.key === 'caseStyle') nativeNames.push(axis.label);
      });
      if (picker) picker.querySelectorAll('.product-form__input').forEach(function (field) {
        var input = field.querySelector('input[type="radio"], select');
        var name = input && input.getAttribute('name');
        if (name && nativeNames.some(function (n) { return n && (name === n || name === 'options[' + n + ']'); })) hide(field);
      });
      section.querySelectorAll('[data-pz-brand-picker], [data-pz-variant-axis-picker="brand"], [data-pz-variant-axis-picker="caseStyle"]').forEach(hide);
      controls.querySelectorAll('[data-pz-control="addon"]').forEach(function (input) {
        if (magsafeKeys.indexOf(input.getAttribute('data-pz-addon-key')) === -1) return;
        var field = input.closest('.pz-field') || input;
        hide(field); field.setAttribute('data-sc-case-native-hidden', legacyAllowed() ? 'false' : 'true');
      });
    }
    // Owner 29/10: the native "MagSafe compatible" add-on stays (below Surface) on Tough Case only,
    // ticked once on entering Tough (an untick is respected); MagSafe/Slim hide and untick it so
    // MagSafe is never paid twice.
    var legacyTicked = null;
    function legacyAllowed() { return selectedCase === 'tough'; }
    function clearLegacy() {
      if (legacyAllowed()) {
        if (busy || legacyTicked === controller) return; // tick after the switch settles, not mid-restore
        var keys = magsafeKeys.filter(function (key) {
          var input = controls && controls.querySelector('[data-pz-control="addon"][data-pz-addon-key="' + key + '"]');
          return input && !input.disabled && !input.closest('[hidden]');
        });
        if (!keys.length) return;
        legacyTicked = controller;
        keys.forEach(function (key) {
          if (!(controller.adapter.serialized.addons && controller.adapter.serialized.addons[key] === true)) controller.adapter.setAddon(key, true);
        });
        return;
      }
      legacyTicked = null;
      magsafeKeys.forEach(function (key) {
        if (controller.adapter.serialized.addons && controller.adapter.serialized.addons[key] === true) controller.adapter.setAddon(key, false);
      });
    }
    function targetNow() { return catalog && core.resolve(catalog, selectedModel, selectedCase); }
    function exact(target) {
      if (!controller || !target || !same(target.productId, cfg.productId || mount.getAttribute('data-pz-product-id'))) return false;
      var state = controller.adapter.serialized;
      var form = doc.getElementById(cfg.formId || mount.getAttribute('data-pz-form-id'));
      var input = form && form.querySelector('[name="id"]');
      return !!(source && mount.getAttribute('data-pz-release-url') === source.releaseUrl
        && same(mount.getAttribute('data-pz-product-id'), target.productId)
        && input && same(input.value, target.variantId)
        && same(mount.getAttribute('data-pz-variant-id'), target.variantId)
        && same(state.brandVariantId, target.variantId) && modelMatches(state.model, target.model)
        && controller.modelConfirmed === true
        && (legacyAllowed() || !magsafeKeys.some(function (key) { return state.addons && state.addons[key] === true; })));
    }
    function valid() {
      var target = targetNow(), price = target && prices[target.sourceKey + '/' + target.variantId];
      return !!(live() && ready && good && !busy && !fatal && !mount.hasAttribute('data-sc-case-failed')
        && !confirmations.template && !confirmations.templateRoute
        && price && price.available === true && exact(target));
    }
    function el(tag, attrs, text) {
      var node = doc.createElement(tag);
      Object.keys(attrs || {}).forEach(function (key) { node.setAttribute(key, attrs[key]); });
      if (text != null) node.textContent = text;
      return node;
    }
    function option(select, value, label) { select.appendChild(el('option', { value: value }, label)); }
    function renderModels() {
      modelSelect.textContent = '';
      option(modelSelect, '', 'Select phone model');
      catalog.models.filter(function (m) { return m.enabled !== false && m.brand === brand; }).forEach(function (m) { option(modelSelect, m.key, m.label); });
      modelSelect.value = selectedModel;
      modelSelect.disabled = !brand;
      syncTrigger();
      // Brand pills mirror the hidden select (single source of truth for brand).
      if (ui) ui.querySelectorAll('[data-sc-case-brand-pill]').forEach(function (pill) { pill.checked = pill.value === brand; });
    }
    function nativeProduct(sourceKey) {
      if (products[sourceKey]) return products[sourceKey];
      var row = catalog.sources[sourceKey];
      products[sourceKey] = request(productURL(row.handle, '.js').href, lifetime.signal).then(function (product) {
        if (!same(product.id, row.productId) || !Array.isArray(product.variants)) fail();
        return product;
      }).catch(function (e) { delete products[sourceKey]; throw e; });
      return products[sourceKey];
    }
    function priceFor(target) {
      return nativeProduct(target.sourceKey).then(function (product) {
        var hits = product.variants.filter(function (variant) { return same(variant.id, target.variantId); });
        if (hits.length !== 1 || !Number.isSafeInteger(hits[0].price) || hits[0].price < 0) fail();
        var variant = hits[0], currency = block.getAttribute('data-currency');
        if (!/^[A-Z]{3}$/.test(currency || '')) fail();
        var result = { available: variant.available === true, price: variant.price,
          text: new Intl.NumberFormat(doc.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(variant.price / 100),
          image: variant.featured_image && variant.featured_image.src || product.featured_image || null,
          compare: Number.isSafeInteger(variant.compare_at_price) && variant.compare_at_price > variant.price
            ? new Intl.NumberFormat(doc.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(variant.compare_at_price / 100) : '' };
        prices[target.sourceKey + '/' + target.variantId] = result;
        return result;
      });
    }
    // The description's "Material" list follows the chosen case (owner 29/10): Tough copy from the
    // Tough PDP, Slim restores the product's own list. No "Material" heading = description left as is.
    var materialList, materialOriginal;
    function syncDescription() {
      if (materialList === undefined) {
        materialList = null;
        var heads = typeof doc.querySelectorAll === 'function' ? doc.querySelectorAll('.product__description p, .product__info-container .rte p') : [];
        for (var i = 0; i < heads.length && !materialList; i++) {
          var next = heads[i].nextElementSibling;
          while (next && next.tagName === 'P' && !next.textContent.trim()) next = next.nextElementSibling;
          if (/^\s*material\s*:?\s*$/i.test(heads[i].textContent) && next && next.tagName === 'UL') materialList = next;
        }
        if (materialList) materialOriginal = Array.prototype.slice.call(materialList.childNodes);
      }
      if (!materialList) return;
      var lines = CASE_FEATURES[selectedModel && selectedCase] || null;
      materialList.textContent = '';
      if (!lines) { materialOriginal.forEach(function (node) { materialList.appendChild(node); }); return; }
      lines.forEach(function (line) { materialList.appendChild(el('li', {}, line)); });
    }
    function legendNow() {
      syncDescription();
      var type = selectedModel && catalog.caseTypes.find(function (t) { return t.key === selectedCase; });
      if (caseLegend) caseLegend.textContent = type ? CARD_LEGENDS[type.key] || type.label : '';
    }
    function renderCards() {
      cards.textContent = ''; legendNow();
      if (!selectedModel) return;
      var modelKey = selectedModel;
      core.caseTypesFor(catalog, modelKey).forEach(function (type) {
        var target = type.target;
        var label = el('label', { class: 'sc-case-selector__card', 'data-sc-case-card': type.key });
        var input = el('input', { type: 'radio', name: uid + '-type', value: type.key, 'data-sc-case-type': type.key });
        input.checked = selectedCase === type.key;
        input.disabled = true;
        var media = el('span', { class: 'sc-case-selector__media', 'aria-hidden': 'true' });
        var image = el('img', { alt: '', width: '160', height: '160', loading: 'lazy' });
        image.hidden = true; media.appendChild(image);
        if (type.key === 'tough' && block.getAttribute('data-sc-case-best-seller') === 'true') label.appendChild(bestSeller());
        var copy = el('span', { class: 'sc-case-selector__copy' });
        var pricing = el('span', { class: 'sc-case-selector__pricing' });
        var price = el('span', { class: 'sc-case-selector__price' }, 'Loading price…');
        var compare = el('s', { class: 'sc-case-selector__compare' }); compare.hidden = true;
        pricing.appendChild(price); pricing.appendChild(compare);
        copy.appendChild(el('span', { class: 'sc-case-selector__name' }, CARD_TITLES[type.key] || type.label));
        var note = block.getAttribute('data-sc-case-note-' + type.key) || '';
        copy.appendChild(el('span', { class: 'sc-case-selector__note' }, note));
        copy.appendChild(pricing);
        label.appendChild(input); label.appendChild(el('span', { class: 'sc-case-selector__tick', 'aria-hidden': 'true' })); label.appendChild(media); label.appendChild(copy);
        cards.appendChild(label);
        input.addEventListener('change', function () { if (input.checked) choose(type.key); });
        priceFor(target).then(function (data) {
          if (!live() || modelKey !== selectedModel || !cards.contains(label)) return;
          price.textContent = data.available ? data.text : data.text + ' · Sold out';
          if (data.compare && data.available) { compare.textContent = data.compare; compare.hidden = false; }
          input.disabled = !data.available;
          var imageURL = block.getAttribute('data-image-' + type.key) || data.image;
          if (block.hasAttribute('data-card-art')) imageURL = ''; // Card art comes from the stylesheet.
          if (imageURL) {
            try {
              var safe = new URL(imageURL, win.location.origin);
              if (safe.protocol === 'https:' || safe.origin === win.location.origin) { image.src = safe.href; image.hidden = false; }
            } catch (imageError) { /* Invalid optional media must not fabricate or erase a valid price. */ }
          }
        }).catch(function () {
          if (live() && cards.contains(label)) { price.textContent = 'Price unavailable'; retry.hidden = false; }
        });
      });
    }
    function syncTrigger() {
      if (!modelTrigger) return;
      var picked = Array.prototype.find.call(modelSelect.options, function (o) { return o.value && o.value === selectedModel; });
      modelValue.textContent = picked ? picked.textContent : modelPrompt();
      if (picked) modelTrigger.setAttribute('data-sc-case-model', picked.value); else modelTrigger.removeAttribute('data-sc-case-model');
      // Not `disabled`: a tap before a brand must still reach needBrand() and show the hint.
      if (brand || groupedPicker()) modelTrigger.removeAttribute('aria-disabled'); else modelTrigger.setAttribute('aria-disabled', 'true');
    }
    // Same badge as the linked-styles card on the Tough PDP (sc-linked-styles__badge--primary).
    function bestSeller() {
      var badge = el('span', { class: 'sc-case-selector__badge' });
      if (!doc.createElementNS) { badge.appendChild(el('span', {}, '\u2605 Best Seller')); return badge; }
      var svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '9'); svg.setAttribute('height', '9'); svg.setAttribute('fill', 'currentColor'); svg.setAttribute('aria-hidden', 'true');
      var path = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z');
      svg.appendChild(path); badge.appendChild(svg); badge.appendChild(el('span', {}, 'Best Seller'));
      return badge;
    }
    function modelPrompt() {
      if (groupedPicker()) return 'Select your model';
      var b = catalog.brands.find(function (row) { return row.key === brand; });
      return 'Select ' + (b ? b.label : 'phone') + ' model';
    }
    // The head prehide style (#sc-case-prehide) keeps the native fields from ever painting; teardown
    // and fatal-without-UI hand them back.
    function releaseNatives() {
      block.removeAttribute('data-sc-case-prehide');
      var style = doc.getElementById && doc.getElementById('sc-case-prehide');
      if (style) style.remove();
    }
    // Same searchable side sheet as the PDP's model picker (pz-model-sheet styles); the hidden
    // select stays the source of truth, a row commit just sets it and fires change.
    function unmount(s) {
      s.root.remove(); s.scrim.remove();
      var i = closing.indexOf(s); if (i !== -1) closing.splice(i, 1);
    }
    function closeSheet(focus) {
      if (!sheet) return;
      var s = sheet; sheet = null;
      if (s.h) { s.h.close(focus); return; } // grouped picker: closes instantly, restores overflow/aria/focus itself
      s.offs.forEach(function (off) { off(); });
      if (doc.body && doc.body.style) doc.body.style.overflow = s.overflow;
      modelTrigger.setAttribute('aria-expanded', 'false');
      s.root.setAttribute('data-pz-sheet-closing', ''); s.scrim.setAttribute('data-pz-sheet-closing', '');
      closing.push(s);
      pageWindow.setTimeout(function () { unmount(s); }, 650);
      if (focus) { try { modelTrigger.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
    }
    // Grouped picker (inline window.ScCaseModelPicker from sc-case-selector-block-v2.liquid, data-sc-case-picker="grouped" on every v2 PDP): all brands of
    // this product, chips filter. A pick from another brand goes through the brand select first (its change
    // re-renders the model options), then the model select; both are the same events a pill/row fire.
    function groupedPicker() {
      var api = pageWindow.ScCaseModelPicker;
      return block.getAttribute('data-sc-case-picker') === 'grouped' && api && typeof api.open === 'function' ? api : null;
    }
    function openGrouped(api) {
      var models = catalog.models.filter(function (m) { return m.enabled !== false && core.caseTypesFor(catalog, m.key).length; });
      var s = {};
      s.h = api.open({ doc: doc, trigger: modelTrigger, brands: catalog.brands, models: models, selected: selectedModel,
        onPick: function (m) {
          if (m.brand !== brand) { brandSelect.value = m.brand; event(brandSelect, 'change'); }
          if (modelSelect.value !== m.key) { modelSelect.value = m.key; event(modelSelect, 'change'); }
        },
        onClose: function () { if (sheet === s) sheet = null; } });
      s.root = s.h.root; s.search = s.h.search;
      sheet = s;
    }
    function openSheet() {
      if (sheet) return;
      var api = groupedPicker();
      if (api) { openGrouped(api); return; }
      if (!brand) return;
      closing.slice().forEach(unmount); // A quick reopen never stacks on a sheet still fading out.
      var s = { offs: [], overflow: doc.body.style.overflow };
      function on(node, type, fn, capture) { node.addEventListener(type, fn, capture); s.offs.push(function () { node.removeEventListener(type, fn, capture); }); }
      s.scrim = el('div', { class: 'pz-model-sheet-scrim', 'aria-hidden': 'true' });
      s.root = el('section', { class: 'pz-model-sheet', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': uid + '-sheet-title', 'data-sc-case-model-sheet': '' });
      var head = el('header', { class: 'pz-model-sheet__head' });
      var close = el('button', { class: 'pz-model-sheet__close', type: 'button', 'aria-label': 'Close model picker' }, '\u00d7');
      head.appendChild(close);
      head.appendChild(el('h2', { class: 'pz-model-sheet__title', id: uid + '-sheet-title' }, modelPrompt()));
      head.appendChild(el('span', { 'aria-hidden': 'true' }));
      var wrap = el('div', { class: 'pz-model-sheet__search-wrap' });
      var search = el('input', { class: 'pz-model-sheet__search', type: 'text', placeholder: 'Type to search', autocomplete: 'off', 'aria-label': 'Search phone models' });
      wrap.appendChild(search);
      var list = el('ul', { class: 'pz-model-sheet__list', role: 'listbox' }), rows = [];
      Array.prototype.forEach.call(modelSelect.options, function (opt) {
        if (!opt.value) return;
        var isSel = opt.value === selectedModel;
        var row = el('li', { class: 'pz-model-sheet__row' + (isSel ? ' pz-model-sheet__row--selected' : ''), role: 'option', tabindex: '0', 'aria-selected': String(isSel), 'data-sc-case-model-option': opt.value });
        row.appendChild(el('span', { class: 'pz-model-sheet__dot', 'aria-hidden': 'true' }));
        row.appendChild(el('span', { class: 'pz-model-sheet__row-label' }, opt.textContent));
        if (isSel) row.appendChild(el('span', { class: 'pz-model-sheet__row-tag' }, 'Selected'));
        function pick() {
          closeSheet(true);
          if (modelSelect.value !== opt.value) { modelSelect.value = opt.value; event(modelSelect, 'change'); }
        }
        on(row, 'click', pick);
        on(row, 'keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
        row._hay = opt.textContent.toLowerCase();
        list.appendChild(row); rows.push(row);
      });
      var empty = el('li', { class: 'pz-model-sheet__empty', role: 'status' }, 'No phone models found');
      empty.hidden = true; list.appendChild(empty);
      on(search, 'input', function () {
        var words = search.value.toLowerCase().trim().split(/\s+/).filter(Boolean), count = 0;
        rows.forEach(function (row) {
          var shown = words.every(function (w) { return row._hay.indexOf(w) !== -1; });
          row.hidden = !shown; if (shown) count++;
        });
        empty.hidden = count !== 0;
      });
      on(s.scrim, 'click', function () { closeSheet(true); });
      on(close, 'click', function () { closeSheet(true); });
      on(doc, 'keydown', function (e) { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeSheet(true); } }, true);
      s.root.appendChild(head); s.root.appendChild(wrap); s.root.appendChild(list);
      doc.body.style.overflow = 'hidden';
      doc.body.appendChild(s.scrim); doc.body.appendChild(s.root);
      modelTrigger.setAttribute('aria-expanded', 'true');
      sheet = s;
      var current = rows.find(function (row) { return row.getAttribute('aria-selected') === 'true'; });
      if (current && list.clientHeight > 0) list.scrollTop = Math.max(0, current.offsetTop - (list.clientHeight - 56) / 2);
      try { close.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    function chevron() {
      if (!doc.createElementNS) return el('span', { class: 'pz-model-combobox__trigger-icon', 'aria-hidden': 'true' });
      var ns = 'http://www.w3.org/2000/svg', svg = doc.createElementNS(ns, 'svg'), path = doc.createElementNS(ns, 'path');
      [['class', 'pz-model-combobox__trigger-icon'], ['width', '14'], ['height', '14'], ['viewBox', '0 0 14 14'], ['fill', 'none'], ['aria-hidden', 'true'], ['focusable', 'false']].forEach(function (a) { svg.setAttribute(a[0], a[1]); });
      path.setAttribute('fill', 'currentColor');
      path.setAttribute('d', 'M7.02 10.4C6.91 10.4 6.81 10.38 6.71 10.34C6.62 10.3 6.53 10.24 6.45 10.16L1.14 4.85C1.03 4.69 0.99 4.51 1 4.32C1.02 4.13 1.11 3.96 1.24 3.83C1.38 3.7 1.55 3.62 1.74 3.6C1.93 3.59 2.11 3.64 2.27 3.75L7.02 8.47L11.77 3.75C11.92 3.66 12.1 3.61 12.27 3.63C12.45 3.65 12.62 3.72 12.75 3.84C12.88 3.97 12.96 4.13 12.99 4.31C13.02 4.48 12.98 4.66 12.9 4.82L7.58 10.13C7.51 10.21 7.42 10.28 7.33 10.32C7.23 10.37 7.12 10.39 7.02 10.4Z');
      svg.appendChild(path);
      return svg;
    }
    function renderUI() {
      ui = el('div', { class: 'sc-case-selector__ui' });
      brandSelect = el('select', { id: uid + '-brand', 'data-sc-case-brand': '', hidden: '', 'aria-hidden': 'true', tabindex: '-1' });
      modelSelect = el('select', { id: uid + '-model', 'data-sc-case-model': '', class: 'pz-field__select pz-model-combobox__native sc-case-selector__model', hidden: '', 'aria-hidden': 'true', tabindex: '-1' });
      modelTrigger = el('button', { type: 'button', id: uid + '-model-trigger', class: 'pz-model-combobox__trigger', 'aria-haspopup': 'dialog', 'aria-expanded': 'false', 'data-sc-case-model-trigger': '' });
      modelValue = el('span', { class: 'pz-model-combobox__value' });
      modelTrigger.appendChild(modelValue); modelTrigger.appendChild(chevron());
      // Same look as the native PDP: brand pills, then the "Phone model *" field, then case cards.
      var brands = el('div', { class: 'product-form__input product-form__input--pills sc-case-selector__brands', role: 'radiogroup', 'aria-label': 'Choose phone brand' });
      var brandHead = el('div', { class: 'product-form__label-container' });
      var brandLabel = el('span', { class: 'form__label font-body-bold' });
      var brandText = el('span', { class: 'form__label-text pz-select-phone__label' });
      brandText.appendChild(el('span', { class: 'sc-case-selector__heading' }, 'Choose phone brand:'));
      brandText.appendChild(el('span', { class: 'pz-select-phone__req', 'aria-hidden': 'true' }, '*'));
      brandLabel.appendChild(brandText);
      brandHead.appendChild(brandLabel); brands.appendChild(brandHead);
      catalog.brands.forEach(function (b, i) {
        var pill = el('input', { type: 'radio', id: uid + '-brand-' + i, name: uid + '-brand-pill', value: b.key, 'data-sc-case-brand-pill': '' });
        brands.appendChild(pill);
        brands.appendChild(el('label', { for: uid + '-brand-' + i, class: 'accent-color-text' }, b.label));
        listen(pill, 'change', function () {
          if (!pill.checked || brandSelect.value === b.key) return;
          brandSelect.value = b.key; event(brandSelect, 'change');
        });
      });
      // Grouped picker (Albert 31/10): no brand pills on the page, the brand follows the picked model (hidden brandSelect).
      if (!groupedPicker()) ui.appendChild(brands);
      ui.appendChild(brandSelect);
      var modelField = el('div', { class: 'pz-field sc-case-selector__model-field' });
      var modelLabel = el('label', { class: 'pz-field__label', for: uid + '-model-trigger' }, groupedPicker() ? 'Phone Model:' : 'Phone model *');
      if (groupedPicker()) modelLabel.appendChild(el('span', { class: 'pz-select-phone__req', 'aria-hidden': 'true' }, '*'));
      modelField.appendChild(modelLabel);
      var combo = el('div', { class: 'pz-model-combobox' });
      combo.appendChild(modelSelect); combo.appendChild(modelTrigger);
      modelField.appendChild(combo);
      // A tap on the model field before any brand explains why nothing opens.
      var brandError = el('p', { class: 'sc-case-selector__error', role: 'alert', id: uid + '-brand-error' });
      brandError.hidden = true;
      function needBrand() {
        if (brand || groupedPicker()) return false;
        brandError.textContent = 'Please select your phone brand above first.'; brandError.hidden = false;
        modelField.classList.add('sc-case-selector__model-field--invalid'); modelTrigger.setAttribute('aria-describedby', brandError.id);
        return true;
      }
      function fieldError(text) {
        brandError.textContent = text; brandError.hidden = !text;
        modelField.classList.toggle('sc-case-selector__model-field--invalid', !!text);
        if (text) modelTrigger.setAttribute('aria-describedby', brandError.id); else modelTrigger.removeAttribute('aria-describedby');
      }
      // Grouped picker has no preselected model: Add to cart / Preview before a model is picked never reach PZ or the
      // cart (the native form still holds the page's first variant); they show the error under the field and open the picker.
      // Window capture: ahead of PZ bootstrap's document-capture handlers.
      function gate(e) {
        if (!groupedPicker() || selectedModel || !live()) return;
        var t = e.type === 'click' ? e.target && e.target.closest && e.target.closest('button[name="add"], [data-pz-preview], [data-pz-sticky-preview-button]') : null;
        var form = e.type === 'submit' ? e.target : t && (t.form || t.closest('form'));
        if (e.type === 'click' && !t) return;
        if (e.type === 'submit' && !(form && form.matches && form.matches('form[action^="/cart/add"]'))) return;
        var at = (t || form).closest('section[data-section]');
        if (at ? at !== section : !t || !t.hasAttribute('data-pz-sticky-preview-button')) return;
        e.preventDefault(); e.stopImmediatePropagation();
        fieldError('Please select your phone model.');
        if (modelTrigger.scrollIntoView) modelTrigger.scrollIntoView({ block: 'center' });
        if (!sheet) openSheet();
      }
      if (groupedPicker() && typeof win.addEventListener === 'function') { listen(win, 'click', gate, true); listen(win, 'submit', gate, true); }
      listen(modelTrigger, 'click', function () { if (needBrand()) return; if (sheet) closeSheet(false); else openSheet(); });
      listen(modelTrigger, 'keydown', function (e) { if (e.key === 'ArrowDown') { e.preventDefault(); if (!needBrand()) openSheet(); } });
      modelField.appendChild(brandError);
      modelField.appendChild(el('p', { class: 'pz-field__help' }, '**Please choose the correct phone model to ensure a perfect match. Your order is made specially for you!'));
      ui.appendChild(modelField);
      var group = el('fieldset', { class: 'sc-case-selector__types' });
      var legend = el('legend', {}, 'Choose case type: ');
      caseLegend = el('span', { class: 'sc-case-selector__chosen' });
      legend.appendChild(caseLegend); group.appendChild(legend);
      cards = el('div', { class: 'sc-case-selector__cards', 'data-sc-case-cards': '' });
      group.appendChild(cards); ui.appendChild(group);
      status = el('p', { role: 'status', 'aria-live': 'polite', class: 'sc-case-selector__status' });
      ui.appendChild(status);
      retry = el('button', { type: 'button', class: 'sc-case-selector__retry', 'data-sc-case-retry': '' }, 'Retry');
      retry.hidden = true; ui.appendChild(retry);
      // v2: the server-rendered first paint has the same geometry; swap in place (no layout shift).
      var ssr = block.querySelector('[data-sc-case-ssr]');
      if (ssr) block.replaceChild(ui, ssr); else block.appendChild(ui);
      dropSSR();
      option(brandSelect, '', 'Select phone brand');
      catalog.brands.forEach(function (b) { option(brandSelect, b.key, b.label); });
      brandSelect.value = brand;
      renderModels(); renderCards();
      listen(brandSelect, 'change', function () {
        abort(); brand = brandSelect.value; selectedModel = ''; selectedCase = '';
        brandError.hidden = true; modelField.classList.remove('sc-case-selector__model-field--invalid'); modelTrigger.removeAttribute('aria-describedby');
        renderModels(); renderCards(); message('Choose your phone model.');
      });
      listen(modelSelect, 'change', function () {
        abort(); selectedModel = modelSelect.value; selectedCase = ''; syncTrigger();
        var model = catalog.models.find(function (m) { return m.key === selectedModel && m.brand === brand && m.enabled !== false; });
        if (!model) { selectedModel = ''; renderCards(); message('Choose your phone model.'); return; }
        fieldError('');
        renderCards();
        // Always one case type: the shopper's last choice if it fits, else nearest MagSafe -> Tough -> Slim.
        // The fallback stays inside this product (same-release switch, no section reload).
        var pick = core.resolve(catalog, selectedModel, preferred) ? preferred : ['magsafe', 'tough', 'snap'].find(function (key) {
          var target = core.resolve(catalog, selectedModel, key);
          return target && same(target.productId, release.product.shopifyProductId);
        });
        // An automatic fallback is not the shopper's choice: keep their preference for the next model.
        if (pick) { var keep = preferred; choose(pick); preferred = keep; }
        else message('Choose a compatible case type. Your previous or default case is unavailable for this model.', true);
      });
      listen(retry, 'click', function () {
        retry.hidden = true;
        if (fatal) { error(); return; }
        renderCards();
        if (selectedCase) choose(selectedCase, !handoffComplete);
      });
      message(restored ? 'Case selected.' : 'Choose your phone brand and model.');
    }
    function dropSSR() {
      Array.prototype.forEach.call(block.querySelectorAll('[data-sc-case-ssr]'), function (node) {
        node.remove();
      });
    }
    // v2 first paint -> shopper state: the checked brand pill, the model the trigger shows (cleared
    // by the inline queue script when the brand changes) and the checked card. Anything the catalog
    // does not accept is dropped, never guessed.
    var queuedSheet = false, ssrPreferred = '', ssrSearch = '';
    // v2.1: the inline fast path's model sheet (data-sc-case-ssr-sheet) never outlives attach; an open
    // one is replaced by the real sheet with the same search text (no double sheet).
    function dropInlineSheet() {
      Array.prototype.forEach.call(doc.querySelectorAll('[data-sc-case-ssr-sheet]'), function (node) {
        if (node.hasAttribute('data-sc-case-model-sheet') && !node.hasAttribute('data-pz-sheet-closing')) {
          var search = node.querySelector('[data-sc-case-model-search], .pz-model-sheet__search');
          ssrSearch = search && search.value || '';
          if (node.__scMp) node.__scMp.close(false); // grouped picker: drops its Esc listener + restores overflow/aria
          if (doc.body && doc.body.style) doc.body.style.overflow = node.getAttribute('data-sc-case-overflow') || '';
        }
        node.remove();
      });
    }
    function adoptSSR() {
      var node = block.querySelector('[data-sc-case-ssr]');
      queuedSheet = block.getAttribute('data-sc-case-queued') === 'sheet';
      block.removeAttribute('data-sc-case-queued');
      if (!node || incoming) return;
      var checked = function (sel) { return Array.prototype.find.call(node.querySelectorAll(sel), function (n) { return n.checked; }); };
      var pill = checked('[data-sc-case-brand-pill]');
      var trigger = node.querySelector('[data-sc-case-model-trigger]'), key = trigger && trigger.getAttribute('data-sc-case-model');
      var keep = block.getAttribute('data-sc-case-pref');
      if (['snap', 'tough', 'magsafe'].indexOf(keep) !== -1) ssrPreferred = keep;
      // Grouped picker: no pills; the brand is the shown model's, and with no model (no preselect) the first pick takes
      // the server's default case (data-sc-case-pref) or the case the shopper tapped before attach.
      var owner = !pill && groupedPicker() && key && catalog.models.find(function (m) { return m.key === key; });
      var b = catalog.brands.find(function (row) { return pill ? row.key === pill.value : owner && row.key === owner.brand; });
      if (!b) { if (!pill && groupedPicker() && ssrPreferred) preferred = ssrPreferred; return; }
      brand = b.key;
      var model = key && catalog.models.find(function (m) { return m.key === key && m.brand === brand && m.enabled !== false; });
      if (!model) return;
      selectedModel = model.key;
      var card = checked('[data-sc-case-type]'), pick = card && card.value;
      if (!(pick && core.resolve(catalog, selectedModel, pick))) {
        pick = [preferred, 'magsafe', 'tough', 'snap'].find(function (k) {
          var target = core.resolve(catalog, selectedModel, k);
          return target && same(target.productId, release.product.shopifyProductId);
        });
      }
      selectedCase = pick || '';
    }
    function waitFor(predicate, token) {
      return new Promise(function (resolve, reject) {
        var started = Date.now();
        function poll() {
          if (!live() || token !== sequence || Date.now() - started > 10000) { reject(new Error('Case selection unavailable')); return; }
          try { if (predicate()) { resolve(); return; } } catch (e) { reject(e); return; }
          win.setTimeout(poll, 25);
        }
        poll();
      });
    }
    function setNativeVariant(target) {
      var table = json(section, '[id="ProductVariants-' + rawSectionId + '"]');
      if (!picker || !Array.isArray(table)) fail();
      var variants = table.filter(function (variant) { return same(variant.id, target.variantId); });
      if (variants.length !== 1 || !Array.isArray(variants[0].options)) fail();
      var fields = Array.from(picker.querySelectorAll('.product-form__input'));
      if (fields.length !== variants[0].options.length) fail();
      var trigger;
      fields.forEach(function (field, index) {
        var value = variants[0].options[index], radios = Array.from(field.querySelectorAll('input[type="radio"]'));
        var select = field.querySelector('select');
        if (radios.length) {
          var hit = radios.filter(function (radio) { return radio.value === value; });
          if (hit.length !== 1) fail();
          radios.forEach(function (radio) { radio.checked = radio === hit[0]; });
          trigger = hit[0];
        } else if (select && Array.from(select.options).some(function (o) { return o.value === value; })) {
          select.value = value; trigger = select;
        } else fail();
      });
      if (!trigger) fail();
      // All native axes form the exact tuple BEFORE Shrine sees the one change; no fallback tuple.
      event(trigger, 'change');
    }
    function rememberSurface() {
      var state = controller.adapter.serialized;
      var model = catalog.models.find(function (m) { return Object.keys(m.targets).some(function (key) {
        var t = m.targets[key];
        return same(t.productId, release.product.shopifyProductId) && same(t.variantId, state.brandVariantId) && modelMatches(t.model, state.model);
      }); });
      if (!model || !state.selects) return;
      var set = release.selection.optionSets.find(function (s) { return s.id === state.model.optionSetId; });
      var selects = {};
      (set && set.options || []).forEach(function (o) {
        var key = set.id + '/' + o.id;
        if (o.label === 'Surface' && o.type === 'Dropdown' && Object.prototype.hasOwnProperty.call(state.selects, key)) selects[key] = state.selects[key];
      });
      if (!Object.keys(selects).length) return;
      surfaceMemory[model.key] = { release: release, selection: { v: state.v,
        brandVariantId: state.brandVariantId, model: clone(state.model), templateUuid: state.templateUuid,
        backgroundId: state.backgroundId, selectedValueByOption: clone(state.selectedValueByOption),
        texts: {}, captures: {}, selects: selects } };
    }
    function mapKeptSelection(fromRelease, toRelease, selection, target) {
      var mapped = core.mapSelection(fromRelease, toRelease, selection, target);
      var memo = surfaceMemory[selectedModel];
      if (memo) {
        var finish = core.mapSelection(memo.release, toRelease, memo.selection, target);
        Object.keys(finish.selects || {}).forEach(function (key) {
          if (key.indexOf(target.model.optionSetId + '/') === 0) {
            if (!mapped.selects) mapped.selects = {};
            mapped.selects[key] = finish.selects[key];
          }
        });
      }
      return mapped;
    }
    async function sameRelease(target, token) {
      var mapped = mapKeptSelection(release, release, clone(controller.adapter.serialized), target);
      clearLegacy();
      var form = doc.getElementById(cfg.formId || mount.getAttribute('data-pz-form-id'));
      if (!form) fail();
      // v2.1: the inline first-paint script may already have set the form id before attach; the
      // mount still carries the landing variant, so the native picker change must still fire.
      if (!same(one(form, '[name="id"]').value, target.variantId)
        || !same(mount.getAttribute('data-pz-variant-id'), target.variantId)) setNativeVariant(target);
      await waitFor(function () { return same(one(form, '[name="id"]').value, target.variantId)
        && same(mount.getAttribute('data-pz-variant-id'), target.variantId); }, token);
      var relay = controller.setVariant(String(target.variantId));
      if (relay !== true) fail();
      var nativeModel = one(controls, '[data-pz-control="model"]');
      if (!Array.from(nativeModel.options).some(function (o) { return same(o.value, target.model.v); })) fail();
      nativeModel.value = String(target.model.v);
      event(nativeModel, 'change'); // This is the runtime's REAL model-confirmation event.
      if (!controller.modelConfirmed) fail();
      // Same controller: committed restore is one-shot. Use its existing adapter restore instead,
      // AFTER explicit native confirmation; mapped retained/hidden answers must not be reset by brand.
      var result = await controller.adapter.restoreSelection(mapped);
      if (!live() || token !== sequence) return;
      clearLegacy();
      if (!result || result.committed !== true || !exact(target)) fail();
      publishCase(selectedCase);
      good = true; lock(false); message('Case selected.');
      clearLegacy();
    }
    async function startSection(next, nextMount) {
      if (nextMount.hasAttribute('data-pz-plugin')) fail(); // Current family mapper certifies schema1.2 only.
      event(next, 'shopify:section:load', { sectionId: rawSectionId });
      if (!pageWindow.__pzBootstrap || typeof pageWindow.__pzBootstrap.refresh !== 'function') fail();
      pageWindow.__pzBootstrap.refresh();
    }
    function retainNativeAnswers(next, nextForm) {
      // PZ answers are mapped by core. Retain ordinary owned Shopify quantity/custom properties
      // too, without copying product ids, submit state, or unrelated forms. Refuse schema loss.
      function fields(scope, formId) {
        return Array.from(scope.querySelectorAll('input, select, textarea')).filter(function (node) {
          var name = node.getAttribute('name') || '';
          var form = node.closest('form');
          return (name === 'quantity' || /^properties\[[^\]]+\]$/.test(name))
            && !node.hasAttribute('data-pz-control')
            && (node.getAttribute('form') === formId || form && form.getAttribute('id') === formId);
        });
      }
      var from = fields(section, cfg.formId || mount.getAttribute('data-pz-form-id'));
      // Shopify's name="id" input shadows HTMLFormElement.id in real browsers.
      var to = fields(next, nextForm.getAttribute('id')), used = [];
      from.forEach(function (field) {
        var kind = field.getAttribute('type') || field.tagName;
        if (kind === 'file' && field.value) fail();
        var matches = to.filter(function (node) {
          return node.getAttribute('name') === field.getAttribute('name')
            && (node.getAttribute('type') || node.tagName) === kind
            && (kind !== 'checkbox' && kind !== 'radio' || node.value === field.value);
        });
        if (matches.length !== 1 || used.indexOf(matches[0]) !== -1) fail();
        var dest = matches[0]; used.push(dest);
        if (kind === 'checkbox' || kind === 'radio') dest.checked = field.checked;
        else if (kind !== 'file') {
          if (dest.tagName === 'SELECT' && (dest.multiple || !Array.from(dest.options).some(function (o) { return o.value === field.value; }))) fail();
          dest.value = field.value;
        }
      });
      if (to.length !== used.length) fail();
    }
    function readDestination(html, target) {
      var sourceRow = catalog.sources[target.sourceKey];
      var parsed = new pageWindow.DOMParser().parseFromString(html, 'text/html');
      var next = one(parsed, 'section[data-section="' + rawSectionId + '"]');
      var nextMount = one(next, '[data-pz-personalizer]');
      if (nextMount.hasAttribute('data-pz-plugin')) fail(); // Refuse before replacing the working source DOM.
      if (nextMount.getAttribute('data-pz-extension') !== mount.getAttribute('data-pz-extension')) fail();
      var nextBlock = one(next, 'sc-case-selector');
      var nextForm = one(next, '[id="' + nextMount.getAttribute('data-pz-form-id') + '"]');
      if (!same(nextMount.getAttribute('data-pz-product-id'), target.productId)
        || !same(nextMount.getAttribute('data-pz-variant-id'), target.variantId)
        || !same(one(nextForm, '[name="id"]').value, target.variantId)
        || nextMount.getAttribute('data-pz-release-url') !== sourceRow.releaseUrl
        || nextBlock.getAttribute('data-family') !== block.getAttribute('data-family')
        || nextBlock.getAttribute('data-theme-id') !== block.getAttribute('data-theme-id')) fail();
      retainNativeAnswers(next, nextForm);
      formLock(nextForm, true);
      // Until the private host hook attaches, even a failed bootstrap cannot submit its default.
      nextForm.addEventListener('submit', function (e) {
        var binding = instances.get(nextMount);
        if (!binding || !binding.valid()) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);
      return { section: next, mount: nextMount, block: nextBlock };
    }
    function waitForAttachment(view) {
      var done = false, watcher, timer, resolve;
      var promise = new Promise(function (yes, no) {
        resolve = function (error) {
          if (done) return; done = true;
          pageWindow.clearTimeout(timer); if (watcher) watcher.disconnect();
          attachmentWaiters.delete(view.mount);
          if (error) no(new Error('Case selection unavailable')); else yes();
        };
        attachmentWaiters.set(view.mount, resolve);
        timer = pageWindow.setTimeout(function () { resolve(true); }, 12000);
        if (pageWindow.MutationObserver) {
          watcher = new pageWindow.MutationObserver(function () {
            if (view.mount.getAttribute('data-pz-state') === 'error') resolve(true);
          });
          watcher.observe(view.mount, { attributes: true });
        }
      });
      promise.catch(function () {}); // A synchronous startup throw still disposes this waiter.
      return { promise: promise, cancel: function () { resolve(); } };
    }
    async function bootDestination(view, record, url) {
      var attachment = waitForAttachment(view);
      try { await startSection(view.section, view.mount); await attachment.promise; }
      catch (e) {
        if (!pageWindow.document.contains(view.section)) return;
        view.mount.setAttribute('data-sc-case-failed', 'true');
        formLock(view.section.querySelector('[id="' + view.mount.getAttribute('data-pz-form-id') + '"]'), true);
        var failedControls = view.section.querySelector('[data-pz-controls]');
        if (failedControls) {
          if (!failedControlLocks.has(failedControls)) failedControlLocks.set(failedControls, failedControls.inert === true);
          failedControls.inert = true;
        }
        var notice = el('p', { role: 'alert', 'data-sc-case-boot-recovery': '' }, 'Case loading failed. Your selection is not ready to purchase.');
        var button = el('button', { type: 'button', class: 'sc-case-selector__retry', 'data-sc-case-boot-recovery': '' }, 'Retry loading case');
        view.block.appendChild(notice); view.block.appendChild(button);
        button.addEventListener('click', async function () {
          if (button.disabled || !pageWindow.document.contains(view.section) || instances.has(view.mount) || !record.selection) return;
          button.disabled = true;
          try {
            var html = await request(url.href, new pageWindow.AbortController().signal, 'html');
            if (!pageWindow.document.contains(view.section) || instances.has(view.mount)) return;
            var replacement = readDestination(html, record.target);
            pending.set(replacement.mount, record);
            event(view.section, 'shopify:section:unload', { sectionId: rawSectionId });
            view.section.parentNode.replaceChild(replacement.section, view.section);
            await bootDestination(replacement, record, url);
          } catch (retryError) { button.disabled = false; }
        });
      } finally { attachment.cancel(); }
    }
    async function crossProduct(target, token) {
      var sourceRow = catalog.sources[target.sourceKey];
      var url = productURL(sourceRow.handle);
      url.searchParams.set('variant', String(target.variantId));
      url.searchParams.set('section_id', rawSectionId);
      var html = await request(url.href, flight.signal, 'html');
      if (!live() || token !== sequence) return;
      var view = readDestination(html, target);
      var record = { sourceRelease: !handoffComplete ? incoming.sourceRelease : release,
        selection: clone(!handoffComplete ? incoming.selection : controller.adapter.serialized),
        target: clone(target), modelKey: selectedModel, caseKey: selectedCase,
        catalog: catalog, family: block.getAttribute('data-family'), surfaceMemory: surfaceMemory, offerChoices: offerChoices,
        confirmations: {
          template: confirmations.template || controller.templateConfirmed === false,
          templateRoute: confirmations.templateRoute || controller.templateRouteConfirmed === false
        } };
      // The pending handoff is private and bound before unload. No answer data in event.detail.
      pending.set(view.mount, record);
      transferring = true;
      event(section, 'shopify:section:unload', { sectionId: rawSectionId });
      if (!section.parentNode) fail();
      section.parentNode.replaceChild(view.section, section);
      destroy();
      await bootDestination(view, record, url);
    }
    async function choose(caseKey, forceSection) {
      if (!ready || dead || fatal) return;
      abort();
      var token = sequence;
      selectedCase = caseKey; preferred = caseKey; legendNow(); legacyTicked = null;
      if (controller) { if (!legacyAllowed()) clearLegacy(); hideNative(); } // Tough ticks once the switch settles
      var target = targetNow();
      if (!target) { selectedCase = ''; renderCards(); message('Choose a compatible case type.'); return; }
      var inPlace = !forceSection && handoffComplete && same(target.productId, release.product.shopifyProductId);
      if (inPlace) freeze(); // before anything can make the runtime re-render
      if (handoffComplete) rememberSurface();
      lock(true); retry.hidden = true; message('Applying your case…');
      cards.querySelectorAll('input').forEach(function (input) { input.checked = input.value === caseKey; });
      flight = new win.AbortController();
      try {
        var price = await priceFor(target);
        if (!live() || token !== sequence) return;
        if (!price.available) fail();
        if (inPlace) await sameRelease(target, token);
        else await crossProduct(target, token);
      } catch (e) { if (live() && token === sequence) error(); }
    }
    function sharedModels(family, shared) {
      if (!shared || shared.version !== 1 || !Array.isArray(shared.models)
        || !Array.isArray(shared.brands) || !Array.isArray(shared.caseTypes)) fail();
      function unique(rows) {
        var seen = new Set();
        rows.forEach(function (row) {
          if (!row || typeof row.key !== 'string' || !row.key || seen.has(row.key)
            || typeof row.label !== 'string' || !row.label.trim()) fail();
          seen.add(row.key);
        });
        return seen;
      }
      var brands = unique(shared.brands), types = unique(shared.caseTypes);
      unique(shared.models);
      var models = shared.models.map(function (model) {
        if (!brands.has(model.brand) || typeof model.enabled !== 'boolean' || !Array.isArray(model.types)
          || new Set(model.types).size !== model.types.length || model.types.some(function (t) { return !types.has(t); })) fail();
        var native = family.models.find(function (row) { return row.key === model.key; });
        var targets = {};
        if (native && native.brand !== model.brand) fail();
        if (native) model.types.forEach(function (type) { if (native.targets[type]) targets[type] = native.targets[type]; });
        return { key: model.key, brand: model.brand, label: model.label,
          enabled: model.enabled && !!native && native.enabled !== false, targets: targets };
      }).filter(function (model) { return Object.keys(model.targets).length > 0; });
      return { version: family.version, sources: family.sources, models: models,
        brands: shared.brands.filter(function (b) { return models.some(function (m) { return m.enabled && m.brand === b.key; }); }),
        caseTypes: shared.caseTypes };
    }
    // v2 product payload (tools/case-selector/apply-sp.mjs) -> the family catalog shape sharedModels() takes.
    function v2Family(t, shared) {
      if (!t || t.version !== 1 || !t.source || !t.targets || typeof t.targets !== 'object' || !Array.isArray(t.sets)
        || !shared || !Array.isArray(shared.models) || !/^[a-z][a-z0-9-]*$/.test(t.source.key || '')) fail();
      var s = t.source, sources = {};
      sources[s.key] = { productId: s.productId, handle: s.handle, releaseUrl: s.releaseUrl, identity: s.identity };
      var models = [];
      shared.models.forEach(function (m) {
        var row = Object.prototype.hasOwnProperty.call(t.targets, m.key) ? t.targets[m.key] : null;
        if (!row) return;
        var targets = {};
        Object.keys(row).forEach(function (type) {
          var r = row[type];
          if (!Array.isArray(r) || r.length !== 4 || !Number.isInteger(r[1]) || t.sets[r[1]] == null) fail();
          targets[type] = { sourceKey: s.key, productId: s.productId, variantId: r[0], model: { optionSetId: t.sets[r[1]], optionId: r[2], v: r[3] } };
        });
        models.push({ key: m.key, brand: m.brand, label: m.label, enabled: true, targets: targets });
      });
      return { version: 1, sources: sources, models: models, brands: shared.brands, caseTypes: shared.caseTypes };
    }
    function catalogInput(marker, urlAttribute, optional) {
      if (block.querySelector('script[' + marker + ']')) return JSON.parse(one(block, 'script[type="application/json"][' + marker + ']').textContent);
      var raw = block.getAttribute(urlAttribute);
      if (!raw) { if (optional) return undefined; fail(); }
      var url = new URL(raw, win.location.origin);
      if (url.protocol !== 'https:' && url.origin !== win.location.origin) fail();
      return request(url.href, lifetime.signal, 'catalog');
    }
    function loadCatalog() {
      admitted = false;
      return Promise.resolve().then(function () {
        core = win.ScCaseSelectorCore;
        if (!core || typeof core.assertCompatible !== 'function' || mount.hasAttribute('data-pz-plugin')) fail();
        core.assertCompatible(release, release);
        requirePresentation();
        if (blocks.length !== 1 || !block.getAttribute('data-family') || !/^[a-zA-Z0-9_-]+$/.test(rawSectionId)) fail();
        if (incoming) return incoming.catalog;
        var v2 = block.querySelector('script[type="application/json"][data-sc-case-v2]');
        if (v2) { var data = JSON.parse(v2.textContent); return sharedModels(v2Family(data.targets, data.models), data.models); }
        // Independent public data: one parallel fetch round, never a two-request waterfall.
        return Promise.all([catalogInput('data-sc-case-catalog', 'data-catalog-url', false),
          catalogInput('data-sc-phone-models', 'data-model-catalog-url', true)]).then(function (data) {
          return data[1] === undefined ? data[0] : sharedModels(data[0], data[1]);
        });
      }).then(function (data) {
      if (dead) fail();
      catalog = data;
      if (!catalog || catalog.version !== 1 || !Array.isArray(catalog.models) || !Array.isArray(catalog.brands) || !catalog.sources) fail();
      var sources = Object.keys(catalog.sources).filter(function (key) { return same(catalog.sources[key].productId, release.product.shopifyProductId); });
      if (sources.length !== 1) fail();
      source = catalog.sources[sources[0]];
      assertSource(source, release, mount);
      var inlineProducts = json(block, 'script[data-sc-case-products]') || [];
      inlineProducts.forEach(function (product) {
        Object.keys(catalog.sources).forEach(function (key) {
          if (same(product.id, catalog.sources[key].productId)) products[key] = Promise.resolve(product);
        });
      });
      if (incoming) {
        var model = catalog.models.find(function (m) { return m.key === incoming.modelKey && m.enabled !== false; });
        if (!model) fail();
        selectedModel = model.key; selectedCase = preferred = incoming.caseKey; brand = model.brand;
      }
      admitted = true;
      return catalog;
      });
    }
    var loaded = loadCatalog();
    loaded.catch(function () { /* attach/restore owns the visible, generic failure */ });
    function restore(nextController) {
      if (!incoming) return Promise.resolve(null);
      if (restorePromise) return restorePromise;
      restorePromise = loaded.then(async function () {
        if (!live() || block.getAttribute('data-family') !== incoming.family) fail();
        var target = core.resolve(catalog, incoming.modelKey, incoming.caseKey);
        if (!target || JSON.stringify(target) !== JSON.stringify(incoming.target)) fail();
        assertSource(catalog.sources[target.sourceKey], release, mount);
        var mapped = mapKeptSelection(incoming.sourceRelease, release, incoming.selection, target);
        var result = await nextController.restoreCommittedSelection(mapped);
        if (!live() || !result || result.committed !== true) fail();
        selectedModel = incoming.modelKey; selectedCase = preferred = incoming.caseKey;
        brand = catalog.models.find(function (m) { return m.key === selectedModel; }).brand;
        restored = true;
        return result;
      }).catch(function () { if (live()) error(); throw new Error('Case selection unavailable'); });
      return restorePromise;
    }
    function attach(options) {
      if (attached || dead) return;
      attached = true;
      controller = options.controller; controls = options.controlsHost;
      var acknowledge = attachmentWaiters.get(mount);
      if (acknowledge) acknowledge();
      // Use the host's private runtime root (including plugin sandbox), not an engine global.
      if (options.root) win = options.root;
      if (options.document) doc = options.document;
      originalInert = failedControlLocks.has(controls) ? failedControlLocks.get(controls) : controls.inert === true;
      failedControlLocks.delete(controls);
      clearBootRecovery(); // A successful native retry now owns recovery via this exact binding.
      if (incoming) lock(true);
      // Extra submit guard is defense-in-depth; host must compose valid() into its purchase validator.
      var form = doc.getElementById(cfg.formId || mount.getAttribute('data-pz-form-id'));
      if (form) listen(form, 'submit', function (e) { if (!valid()) { e.preventDefault(); e.stopImmediatePropagation(); } }, true);
      function finishAttachment() {
        Promise.all([loaded, options.startFlight]).then(function () {
        if (!live()) return;
        if (incoming && !restored) fail();
        if (picker) picker.setAttribute('data-update-url', 'false');
        if (!restored) { adoptSSR(); dropInlineSheet(); }
        block.removeAttribute('data-sc-case-pref'); block.removeAttribute('data-sc-case-picked');
        renderUI(); ready = true;
        clearLegacy(); hideNative();
        if (typeof controller.adapter.on === 'function') offs.push(controller.adapter.on('state', function () { clearLegacy(); hideNative(); }));
        if (win.MutationObserver) { observer = new win.MutationObserver(hideNative); observer.observe(controls, { childList: true, subtree: true }); }
        function confirmNative(e) {
          var axis = e.target && e.target.getAttribute && e.target.getAttribute('data-pz-control');
          if (axis === 'template') confirmations.template = false;
          if (axis === 'template-route') confirmations.templateRoute = false;
          if (!confirmations.template && !confirmations.templateRoute && good) message('Case selected.');
        }
        listen(controls, 'change', confirmNative);
        listen(controls, 'click', confirmNative);
        if (restored) {
          publishCase(selectedCase);
          // After the case media is published, so the restored design wins the gallery.
          if (typeof options.revealRestored === 'function') options.revealRestored();
          good = true; lock(false); modelTrigger.focus();
          if (confirmations.template || confirmations.templateRoute) message('Case selected. Please choose the required personalization options before purchasing.', true);
        }
        block.setAttribute('data-sc-case-ready', 'true');
        // Replay the v2 first paint: apply the preselected (or pre-attach tapped) case, reopen a
        // model sheet tapped before attach.
        // A fast-path automatic fallback (model without the shopper's case) keeps their preference.
        if (!restored && selectedModel && selectedCase) { choose(selectedCase); if (ssrPreferred) preferred = ssrPreferred; }
        if (queuedSheet && (brand || groupedPicker())) {
          queuedSheet = false; openSheet();
          var search = sheet && (sheet.search || sheet.root.querySelector('.pz-model-sheet__search'));
          if (search && ssrSearch) { search.value = ssrSearch; event(search, 'input'); }
        }
      }).catch(function () {
        if (!live()) return;
        fatal = !admitted;
        if (!ui && admitted) { renderUI(); ready = true; }
        if (!ui) {
          dropSSR();
          releaseNatives(); // No selector UI: give the native fields back.
          status = el('p', { role: 'alert' }); block.appendChild(status);
          retry = el('button', { type: 'button', class: 'sc-case-selector__retry', 'data-sc-case-retry': '' }, 'Retry');
          block.appendChild(retry);
          listen(retry, 'click', function () {
            if (!live()) return;
            status.remove(); retry.remove(); status = null; retry = null;
            fatal = false;
            loaded = loadCatalog();
            finishAttachment();
          });
        }
        error();
        });
      }
      finishAttachment();
    }
    function destroy() {
      if (dead) return;
      dead = true; good = false; sequence++;
      lifetime.abort(); if (flight && !transferring) flight.abort();
      unfreeze();
      if (observer) observer.disconnect();
      var waiting = attachmentWaiters.get(mount); if (waiting) waiting(true);
      formLock(ownedForm, false);
      if (presentation) presentation.destroy();
      block.removeAttribute('data-sc-case-selected');
      offs.splice(0).forEach(function (off) { if (typeof off === 'function') off(); });
      hidden.forEach(function (row) {
        if (row.old == null) row.node.removeAttribute('data-sc-case-native-hidden');
        else row.node.setAttribute('data-sc-case-native-hidden', row.old);
      });
      if (controls) controls.inert = originalInert;
      if (picker) {
        if (originalUpdateURL == null) picker.removeAttribute('data-update-url');
        else picker.setAttribute('data-update-url', originalUpdateURL);
      }
      closeSheet(false); closing.slice().forEach(unmount);
      releaseNatives();
      if (ui) ui.remove();
      else { if (status) status.remove(); if (retry) retry.remove(); }
      block.removeAttribute('data-sc-case-ready'); block.removeAttribute('aria-busy');
      pending.delete(mount); instances.delete(mount);
      incoming = null; controller = null;
    }
    var api = { transition: !!incoming, restore: restore, attach: attach, valid: valid, destroy: destroy };
    instances.set(mount, api);
    return api;
  }
  return { prepare: prepare };
}));
// Early-tap nudge (v2 PDPs only; PZ core untouched). PZ already holds an Add to cart tapped before it is ready and
// replays it when the design is valid; when names are still empty it only rewrites its notice to "Please complete
// the highlighted…" and drops the tap, with nothing highlighted. Here: re-tap Add to cart ONCE so PZ's own purchase
// validation scrolls to, focuses and marks the empty required field. It never adds to cart: it only fires while a
// visible required PZ field is empty, and a native submit escaping that synthetic tap is cancelled.
(function (root) {
  'use strict';
  var doc = root && root.document;
  if (!doc || !root.MutationObserver || typeof root.addEventListener !== 'function' || typeof root.setTimeout !== 'function' ||
      doc.__scCaseAtcNudge) return;
  doc.__scCaseAtcNudge = 1;
  var WINDOW_MS = 10000, LOADING = 'Loading your design… we’ll check your details automatically.';
  var tapped = 0;
  function atcOf(n) { return n && n.closest && n.closest('form[action^="/cart/add"] button[name="add"]'); }
  function section(btn) { return btn.closest('section[data-section]'); }
  function ours(btn) { var s = section(btn); return !!(s && s.querySelector('sc-case-selector')); }
  // window capture runs before PZ bootstrap's document-capture hold, which stops the tap's propagation.
  root.addEventListener('click', function (e) { if (e.isTrusted && atcOf(e.target)) tapped = Date.now(); }, true);
  root.addEventListener('pageshow', function (e) { if (e.persisted) tapped = 0; });
  function emptyRequired(sec) {
    return [].some.call(sec.querySelectorAll('[data-pz-control][required]'), function (n) {
      return !n.disabled && n.getClientRects().length > 0 && !String(n.value || '').trim();
    });
  }
  function nudge(btn, notice) {
    var sec = section(btn), block = sec.querySelector('sc-case-selector');
    var at = Math.max(tapped, Number(btn.getAttribute('data-sc-case-tap')) || 0);
    btn.removeAttribute('data-sc-case-tap');
    if (!block || !block.hasAttribute('data-sc-case-ready') || btn.disabled || btn.getAttribute('aria-disabled') === 'true' ||
        doc.visibilityState !== 'visible' || !at || Date.now() - at > WINDOW_MS || !emptyRequired(sec)) {
      notice.textContent = 'Please check your personalization options above, then tap Add to cart.';
      return;
    }
    // On the form (capture), added after PZ's own form-capture submit validation, so PZ still validates/scrolls first;
    // if PZ is not bound, the guard stops the native submit and the theme's fetch-add (which carries no properties).
    var form = btn.form;
    function guard(e) { if (e.target === form) { e.preventDefault(); e.stopImmediatePropagation(); } }
    if (form) form.addEventListener('submit', guard, true);
    try { btn.click(); } finally { if (form) form.removeEventListener('submit', guard, true); }
  }
  function sync(btn) {
    var sec = section(btn), notice = sec && sec.querySelector('.pz-atc-loading-notice');
    var text = notice ? notice.textContent : '';
    if (notice && /^Loading your design/.test(text)) {
      notice.setAttribute('data-sc-case-loading', '');
      btn.setAttribute('data-sc-case-busy', '');
      if (text !== LOADING) notice.textContent = LOADING;
      return;
    }
    btn.removeAttribute('data-sc-case-busy');
    if (!notice) return;
    notice.removeAttribute('data-sc-case-loading');
    if (/^Please complete the highlighted/.test(text) && !notice.__scCaseNudged) {
      notice.__scCaseNudged = 1;
      nudge(btn, notice);
      // PZ never returns to "Loading" after ready: stop watching once the tap window has passed.
      root.setTimeout(function () {
        if (btn.__scCaseObserver) btn.__scCaseObserver.disconnect();
        btn.removeAttribute('data-sc-case-busy');
        notice.removeAttribute('data-sc-case-loading');
      }, WINDOW_MS);
    }
  }
  function watch() {
    [].forEach.call(doc.querySelectorAll('form[action^="/cart/add"] button[name="add"]'), function (btn) {
      if (btn.__scCaseWatched || !ours(btn)) return;
      btn.__scCaseWatched = 1;
      // PZ puts its notice after the button (tap) or after the form (submit): watch the product section.
      btn.__scCaseObserver = new root.MutationObserver(function () { sync(btn); });
      btn.__scCaseObserver.observe(section(btn), { childList: true, subtree: true, characterData: true });
      sync(btn);
    });
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', watch); else watch();
}(typeof self !== 'undefined' ? self : this));
